import { BaseWindow } from './BaseWindow.js';
import { getAllConfig } from '../config.js';
import { CONFIG_METADATA, ConfigTypes, UIControls, getMetadataForValue, formatValue, validateValue } from '../utils/configMetadata.js';

/**
 * Configuration window using BaseWindow system
 */
export class ConfigWindow extends BaseWindow {
  constructor() {
    super({
      id: 'configWindow',
      title: 'GAME CONFIGURATION',
      name: 'Configuration',
      width: 800,
      height: 600,
      minWidth: 600,
      minHeight: 400,
      onInit: () => this.initConfig()
    });
    
    // Config state
    this.activeTopTab = localStorage.getItem('soulforge_configActiveTopTab') || 'gameplay';
    this.activeSubTabs = {
      gameplay: localStorage.getItem('soulforge_configActiveSubTab_gameplay') || 'GAME_CONFIG',
      entities: localStorage.getItem('soulforge_configActiveSubTab_entities') || 'BASE_CONFIG',
      visuals: localStorage.getItem('soulforge_configActiveSubTab_visuals') || 'RENDER_CONFIG',
      balance: localStorage.getItem('soulforge_configActiveSubTab_balance') || 'THRESHOLD_CONFIG',
      debugging: localStorage.getItem('soulforge_configActiveSubTab_debugging') || 'DEBUG_CONFIG'
    };
    this.config = getAllConfig();
    this.originalConfig = JSON.parse(JSON.stringify(this.config));
    this.modifiedValues = new Set();
    
    // Organize configs into categories
    this.configCategories = {
      gameplay: {
        label: 'Gameplay',
        configs: {
          'GAME_CONFIG': 'Core Game',
          'TEAM_CONFIG': 'Teams',
          'AI_CONFIG': 'AI Behavior',
          'COMBAT_CONFIG': 'Combat',
          'MOVEMENT_CONFIG': 'Movement'
        }
      },
      entities: {
        label: 'Entities',
        configs: {
          'BASE_CONFIG': 'Bases',
          'RESOURCE_CONFIG': 'Resources',
          'PROJECTILE_CONFIG': 'Projectiles',
          'BERSERKER_CONFIG': 'Berserkers'
        }
      },
      visuals: {
        label: 'Visuals',
        configs: {
          'RENDER_CONFIG': 'Rendering',
          'THREE_RENDERER_CONFIG': '3D Renderer',
          'ANIMATION_CONFIG': 'Animations',
          'UI_CONFIG': 'User Interface'
        }
      },
      balance: {
        label: 'Balance',
        configs: {
          'THRESHOLD_CONFIG': 'Thresholds',
          'LEVEL_CONFIG': 'Leveling',
          'ECONOMY_CONFIG': 'Economy'
        }
      },
      debugging: {
        label: 'Debug',
        configs: {
          'DEBUG_CONFIG': 'Debug Settings',
          'PERFORMANCE_CONFIG': 'Performance'
        }
      }
    };
  }
  
  /**
   * Initialize config after window creation
   */
  initConfig() {
    // Add styles
    this.addStyles();
    
    // Initialize tabs
    this.createTopTabs();
    this.updateSubTabs();
    
    // Load initial content
    this.loadTabContent(this.activeSubTabs[this.activeTopTab]);
    
    // Update modified count
    this.updateModifiedCount();
  }
  
  /**
   * Override createTitleBar to add minimize button
   */
  createTitleBar() {
    const titleBar = document.createElement('div');
    titleBar.className = 'config-window-title';
    titleBar.innerHTML = `
      <span>${this.windowTitle}</span>
      <div class="config-title-buttons">
        <button id="configMinimizeBtn" title="Minimize (−)">−</button>
        <button id="closeConfigWindow" title="Close (×)">×</button>
      </div>
    `;
    
    // Handle minimize button
    const minimizeBtn = titleBar.querySelector('#configMinimizeBtn');
    minimizeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.minimize();
    });
    
    // Handle close button
    const closeBtn = titleBar.querySelector('#closeConfigWindow');
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.hide();
    });
    
    return titleBar;
  }
  
  /**
   * Create content area
   */
  createContent() {
    const content = document.createElement('div');
    content.className = 'config-content';
    content.innerHTML = `
      <div id="configTopTabBar"></div>
      <div id="configSubTabBar"></div>
      <div id="configTabContent"></div>
      <div id="configActionBar">
        <div class="config-actions-left">
          <button id="configImportBtn" class="config-action-btn">Import</button>
          <button id="configExportBtn" class="config-action-btn">Export</button>
          <button id="configPresetsBtn" class="config-action-btn">Presets</button>
        </div>
        <div class="config-actions-right">
          <span id="configModifiedCount"></span>
          <button id="configResetBtn" class="config-action-btn">Reset All</button>
          <button id="configSaveBtn" class="config-action-btn">Save Changes</button>
        </div>
      </div>
    `;
    
    // Store references
    this.topTabBar = content.querySelector('#configTopTabBar');
    this.subTabBar = content.querySelector('#configSubTabBar');
    this.tabContent = content.querySelector('#configTabContent');
    this.modifiedCountEl = content.querySelector('#configModifiedCount');
    
    // Set up event handlers
    this.setupEventHandlers(content);
    
    return content;
  }
  
  /**
   * Add config-specific styles
   */
  addStyles() {
    if (document.getElementById('config-window-styles')) return;
    
    const style = document.createElement('style');
    style.id = 'config-window-styles';
    style.textContent = `
      .config-window-title {
        background: rgba(0, 255, 0, 0.1);
        border-bottom: 1px solid #0f0;
        padding: 8px 12px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        user-select: none;
      }
      
      .config-title-buttons {
        display: flex;
        gap: 8px;
      }
      
      .config-title-buttons button {
        background: transparent;
        border: 1px solid #0f0;
        color: #0f0;
        padding: 2px 8px;
        cursor: pointer;
        font-size: 14px;
        line-height: 1;
      }
      
      .config-title-buttons button:hover {
        background: rgba(0, 255, 0, 0.1);
      }
      
      .config-content {
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;
      }
      
      #configTopTabBar, #configSubTabBar {
        display: flex;
        border-bottom: 1px solid #0f0;
        background: rgba(0, 255, 0, 0.05);
      }
      
      .config-top-tab, .config-sub-tab {
        padding: 8px 16px;
        background: transparent;
        border: none;
        border-right: 1px solid rgba(0, 255, 0, 0.2);
        color: rgba(0, 255, 0, 0.6);
        cursor: pointer;
        font-family: 'Berkeley Mono', 'Courier New', monospace;
        font-size: 10px;
        text-transform: uppercase;
        transition: all 0.2s;
      }
      
      .config-top-tab:hover, .config-sub-tab:hover {
        background: rgba(0, 255, 0, 0.1);
        color: #0f0;
      }
      
      .config-top-tab.active, .config-sub-tab.active {
        background: rgba(0, 255, 0, 0.15);
        color: #0f0;
        font-weight: bold;
      }
      
      #configTabContent {
        flex: 1;
        overflow-y: auto;
        padding: 20px;
      }
      
      #configActionBar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px;
        border-top: 1px solid #0f0;
        background: rgba(0, 255, 0, 0.05);
      }
      
      .config-actions-left, .config-actions-right {
        display: flex;
        gap: 8px;
        align-items: center;
      }
      
      .config-action-btn {
        padding: 6px 12px;
        background: transparent;
        border: 1px solid #0f0;
        color: #0f0;
        cursor: pointer;
        font-family: 'Berkeley Mono', 'Courier New', monospace;
        font-size: 10px;
        text-transform: uppercase;
        transition: all 0.2s;
      }
      
      .config-action-btn:hover {
        background: rgba(0, 255, 0, 0.1);
        box-shadow: 0 0 10px rgba(0, 255, 0, 0.3);
      }
      
      #configModifiedCount {
        color: #ff0;
        margin-right: 10px;
        font-size: 10px;
      }
      
      /* Config item styles */
      .config-section {
        margin-bottom: 30px;
      }
      
      .config-section-title {
        color: #0f0;
        font-size: 12px;
        font-weight: bold;
        text-transform: uppercase;
        margin-bottom: 15px;
        padding-bottom: 5px;
        border-bottom: 1px solid rgba(0, 255, 0, 0.3);
      }
      
      .config-item {
        margin-bottom: 15px;
        padding: 10px;
        background: rgba(0, 255, 0, 0.02);
        border: 1px solid transparent;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        gap: 15px;
      }
      
      .config-item:hover {
        background: rgba(0, 255, 0, 0.05);
        border-color: rgba(0, 255, 0, 0.2);
      }
      
      .config-item.modified {
        background: rgba(255, 255, 0, 0.05);
        border-color: rgba(255, 255, 0, 0.3);
      }
      
      .config-label {
        flex: 1;
        color: #0f0;
        font-size: 10px;
        text-transform: uppercase;
      }
      
      .config-input, .config-slider {
        width: 100px;
        background: #000;
        border: 1px solid #0f0;
        color: #0f0;
        padding: 4px 8px;
        font-family: 'Berkeley Mono', 'Courier New', monospace;
        font-size: 10px;
      }
      
      .config-input.modified, .config-slider.modified {
        border-color: #ff0;
        color: #ff0;
      }
      
      .config-checkbox {
        width: 20px;
        height: 20px;
        background: #000;
        border: 1px solid #0f0;
        cursor: pointer;
        position: relative;
      }
      
      .config-checkbox.checked::after {
        content: '✓';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: #0f0;
        font-size: 14px;
      }
      
      .config-checkbox.modified {
        border-color: #ff0;
      }
      
      .config-help {
        color: rgba(0, 255, 0, 0.6);
        cursor: help;
        font-size: 12px;
      }
      
      .config-help:hover {
        color: #0f0;
      }
      
      .config-value-display {
        min-width: 60px;
        text-align: right;
        color: #0f0;
        font-size: 10px;
      }
      
      .config-value-display.modified {
        color: #ff0;
      }
      
      .config-unit {
        color: rgba(0, 255, 0, 0.6);
        font-size: 9px;
      }
    `;
    document.head.appendChild(style);
  }
  
  /**
   * Create top level tabs
   */
  createTopTabs() {
    this.topTabBar.innerHTML = '';
    
    Object.entries(this.configCategories).forEach(([key, category]) => {
      const tab = document.createElement('button');
      tab.className = `config-top-tab ${key === this.activeTopTab ? 'active' : ''}`;
      tab.textContent = category.label;
      tab.dataset.categoryKey = key;
      
      tab.addEventListener('click', () => {
        // Update active top tab
        this.topTabBar.querySelectorAll('.config-top-tab').forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        this.activeTopTab = key;
        localStorage.setItem('soulforge_configActiveTopTab', key);
        
        // Update sub tabs
        this.updateSubTabs();
        
        // Load content for active sub tab
        this.loadTabContent(this.activeSubTabs[key]);
      });
      
      this.topTabBar.appendChild(tab);
    });
  }
  
  /**
   * Update sub tabs based on active top tab
   */
  updateSubTabs() {
    this.subTabBar.innerHTML = '';
    
    const category = this.configCategories[this.activeTopTab];
    const activeSubTab = this.activeSubTabs[this.activeTopTab];
    
    Object.entries(category.configs).forEach(([configKey, label]) => {
      // Only show configs that exist
      if (!this.config[configKey]) return;
      
      const tab = document.createElement('button');
      tab.className = `config-sub-tab ${configKey === activeSubTab ? 'active' : ''}`;
      tab.textContent = label;
      tab.dataset.configKey = configKey;
      
      tab.addEventListener('click', () => {
        // Update active sub tab
        this.subTabBar.querySelectorAll('.config-sub-tab').forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        this.activeSubTabs[this.activeTopTab] = configKey;
        localStorage.setItem(`soulforge_configActiveSubTab_${this.activeTopTab}`, configKey);
        
        // Load content
        this.loadTabContent(configKey);
      });
      
      this.subTabBar.appendChild(tab);
    });
  }
  
  /**
   * Load content for a specific config tab
   */
  loadTabContent(configKey) {
    this.tabContent.innerHTML = '';
    
    const configData = this.config[configKey];
    if (!configData) return;
    
    const section = document.createElement('div');
    section.className = 'config-section';
    
    const title = document.createElement('div');
    title.className = 'config-section-title';
    title.textContent = `${configKey.replace(/_/g, ' ')} Settings`;
    section.appendChild(title);
    
    // Create config items
    Object.entries(configData).forEach(([key, value]) => {
      const metadata = getMetadataForValue(configKey, key);
      if (!metadata) return; // Skip if no metadata
      
      const item = this.createConfigItem(configKey, key, value, metadata);
      section.appendChild(item);
    });
    
    this.tabContent.appendChild(section);
  }
  
  /**
   * Create a single config item UI
   */
  createConfigItem(configKey, key, value, metadata) {
    const item = document.createElement('div');
    item.className = 'config-item';
    if (this.modifiedValues.has(`${configKey}.${key}`)) {
      item.classList.add('modified');
    }
    
    const label = document.createElement('label');
    label.className = 'config-label';
    label.textContent = key.replace(/_/g, ' ').toLowerCase();
    item.appendChild(label);
    
    // Create appropriate control based on UI type
    let control;
    switch (metadata.ui) {
      case UIControls.SLIDER:
        control = this.createSlider(configKey, key, value, metadata);
        break;
      case UIControls.NUMBER:
        control = this.createNumberInput(configKey, key, value, metadata);
        break;
      case UIControls.CHECKBOX:
        control = this.createCheckbox(configKey, key, value, metadata);
        break;
      case UIControls.COLOR:
        control = this.createColorInput(configKey, key, value, metadata);
        break;
      case UIControls.SELECT:
        control = this.createSelect(configKey, key, value, metadata);
        break;
      default:
        control = this.createNumberInput(configKey, key, value, metadata);
    }
    
    item.appendChild(control);
    
    // Add help tooltip if available
    if (metadata.help) {
      const help = document.createElement('span');
      help.className = 'config-help';
      help.textContent = '?';
      help.title = metadata.help;
      item.appendChild(help);
    }
    
    return item;
  }
  
  /**
   * Create slider control
   */
  createSlider(configKey, key, value, metadata) {
    const container = document.createElement('div');
    container.style.display = 'flex';
    container.style.alignItems = 'center';
    container.style.gap = '10px';
    
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.className = 'config-slider';
    slider.min = metadata.min;
    slider.max = metadata.max;
    slider.step = metadata.step || 1;
    slider.value = value;
    
    if (this.modifiedValues.has(`${configKey}.${key}`)) {
      slider.classList.add('modified');
    }
    
    const display = document.createElement('span');
    display.className = 'config-value-display';
    if (this.modifiedValues.has(`${configKey}.${key}`)) {
      display.classList.add('modified');
    }
    display.textContent = formatValue(value, metadata);
    
    slider.addEventListener('input', () => {
      const newValue = validateValue(parseFloat(slider.value), metadata);
      this.updateValue(configKey, key, newValue);
      display.textContent = formatValue(newValue, metadata);
      display.classList.add('modified');
      slider.classList.add('modified');
    });
    
    container.appendChild(slider);
    container.appendChild(display);
    
    if (metadata.unit) {
      const unit = document.createElement('span');
      unit.className = 'config-unit';
      unit.textContent = metadata.unit;
      container.appendChild(unit);
    }
    
    return container;
  }
  
  /**
   * Create number input control
   */
  createNumberInput(configKey, key, value, metadata) {
    const input = document.createElement('input');
    input.type = 'number';
    input.className = 'config-input';
    input.min = metadata.min;
    input.max = metadata.max;
    input.step = metadata.step || 1;
    input.value = value;
    
    if (this.modifiedValues.has(`${configKey}.${key}`)) {
      input.classList.add('modified');
    }
    
    input.addEventListener('change', () => {
      const newValue = validateValue(parseFloat(input.value), metadata);
      this.updateValue(configKey, key, newValue);
      input.value = newValue;
      input.classList.add('modified');
    });
    
    const container = document.createElement('div');
    container.style.display = 'flex';
    container.style.alignItems = 'center';
    container.style.gap = '5px';
    container.appendChild(input);
    
    if (metadata.unit) {
      const unit = document.createElement('span');
      unit.className = 'config-unit';
      unit.textContent = metadata.unit;
      container.appendChild(unit);
    }
    
    return container;
  }
  
  /**
   * Create checkbox control
   */
  createCheckbox(configKey, key, value, metadata) {
    const checkbox = document.createElement('div');
    checkbox.className = 'config-checkbox';
    if (value) checkbox.classList.add('checked');
    if (this.modifiedValues.has(`${configKey}.${key}`)) {
      checkbox.classList.add('modified');
    }
    
    checkbox.addEventListener('click', () => {
      const newValue = !this.config[configKey][key];
      this.updateValue(configKey, key, newValue);
      checkbox.classList.toggle('checked');
      checkbox.classList.add('modified');
    });
    
    return checkbox;
  }
  
  /**
   * Create color input control
   */
  createColorInput(configKey, key, value, metadata) {
    const container = document.createElement('div');
    container.style.display = 'flex';
    container.style.alignItems = 'center';
    container.style.gap = '10px';
    
    const colorDisplay = document.createElement('div');
    colorDisplay.className = 'config-color';
    colorDisplay.style.width = '30px';
    colorDisplay.style.height = '20px';
    colorDisplay.style.backgroundColor = value;
    if (this.modifiedValues.has(`${configKey}.${key}`)) {
      colorDisplay.classList.add('modified');
    }
    
    const input = document.createElement('input');
    input.className = 'config-input';
    input.value = value;
    if (this.modifiedValues.has(`${configKey}.${key}`)) {
      input.classList.add('modified');
    }
    
    input.addEventListener('change', () => {
      const newValue = input.value;
      this.updateValue(configKey, key, newValue);
      colorDisplay.style.backgroundColor = newValue;
      colorDisplay.classList.add('modified');
      input.classList.add('modified');
    });
    
    container.appendChild(colorDisplay);
    container.appendChild(input);
    
    return container;
  }
  
  /**
   * Create select control
   */
  createSelect(configKey, key, value, metadata) {
    const select = document.createElement('select');
    select.className = 'config-input';
    
    metadata.options.forEach(option => {
      const optionEl = document.createElement('option');
      optionEl.value = option.value;
      optionEl.textContent = option.label;
      if (option.value === value) {
        optionEl.selected = true;
      }
      select.appendChild(optionEl);
    });
    
    if (this.modifiedValues.has(`${configKey}.${key}`)) {
      select.classList.add('modified');
    }
    
    select.addEventListener('change', () => {
      const newValue = select.value;
      this.updateValue(configKey, key, newValue);
      select.classList.add('modified');
    });
    
    return select;
  }
  
  /**
   * Update a config value
   */
  updateValue(configKey, key, value) {
    this.config[configKey][key] = value;
    this.modifiedValues.add(`${configKey}.${key}`);
    this.updateModifiedCount();
  }
  
  /**
   * Update modified count display
   */
  updateModifiedCount() {
    const count = this.modifiedValues.size;
    if (count > 0) {
      this.modifiedCountEl.textContent = `${count} modified`;
      this.modifiedCountEl.style.display = 'inline';
    } else {
      this.modifiedCountEl.style.display = 'none';
    }
  }
  
  /**
   * Set up event handlers
   */
  setupEventHandlers(content) {
    // Import button
    content.querySelector('#configImportBtn').addEventListener('click', () => {
      this.importConfig();
    });
    
    // Export button
    content.querySelector('#configExportBtn').addEventListener('click', () => {
      this.exportConfig();
    });
    
    // Presets button
    content.querySelector('#configPresetsBtn').addEventListener('click', () => {
      console.log('Presets not implemented yet');
    });
    
    // Reset button
    content.querySelector('#configResetBtn').addEventListener('click', () => {
      this.resetConfig();
    });
    
    // Save button
    content.querySelector('#configSaveBtn').addEventListener('click', () => {
      this.saveConfig();
    });
  }
  
  /**
   * Import configuration from file
   */
  importConfig() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    
    input.addEventListener('change', (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const imported = JSON.parse(e.target.result);
          // Merge with existing config
          Object.keys(imported).forEach(configKey => {
            if (this.config[configKey]) {
              Object.keys(imported[configKey]).forEach(key => {
                if (this.config[configKey].hasOwnProperty(key)) {
                  this.config[configKey][key] = imported[configKey][key];
                  this.modifiedValues.add(`${configKey}.${key}`);
                }
              });
            }
          });
          
          // Reload current tab
          this.loadTabContent(this.activeSubTabs[this.activeTopTab]);
          this.updateModifiedCount();
          
          console.log('Configuration imported successfully');
        } catch (err) {
          console.error('Failed to import configuration:', err);
          alert('Failed to import configuration. Please check the file format.');
        }
      };
      
      reader.readAsText(file);
    });
    
    input.click();
  }
  
  /**
   * Export current configuration
   */
  exportConfig() {
    const data = JSON.stringify(this.config, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `soulforge-config-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    
    URL.revokeObjectURL(url);
  }
  
  /**
   * Reset all configuration values
   */
  resetConfig() {
    if (!confirm('Reset all configuration values to defaults? This cannot be undone.')) {
      return;
    }
    
    // Reset to original values
    this.config = JSON.parse(JSON.stringify(this.originalConfig));
    this.modifiedValues.clear();
    
    // Reload current tab
    this.loadTabContent(this.activeSubTabs[this.activeTopTab]);
    this.updateModifiedCount();
    
    console.log('Configuration reset to defaults');
  }
  
  /**
   * Save configuration changes
   */
  saveConfig() {
    if (this.modifiedValues.size === 0) {
      alert('No changes to save');
      return;
    }
    
    // In a real implementation, this would save to a server or localStorage
    console.log('Saving configuration...', this.config);
    
    // For now, just save to localStorage
    localStorage.setItem('soulforge_gameConfig', JSON.stringify(this.config));
    
    // Clear modified state
    this.modifiedValues.clear();
    this.updateModifiedCount();
    
    // Remove modified styling
    this.container.querySelectorAll('.modified').forEach(el => {
      el.classList.remove('modified');
    });
    
    alert('Configuration saved. Reload the game to apply changes.');
  }
  
  /**
   * Minimize window (custom behavior)
   */
  minimize() {
    // Could implement actual minimize behavior
    console.log('Minimize not implemented');
  }
  
  /**
   * Toggle window visibility with C key
   * Note: This is now handled by InputManager.js to avoid duplicate handlers
   */
  static setupKeyboardShortcut() {
    // Keyboard shortcut is handled by InputManager.js
  }
}

// Create singleton instance
export const configWindow = new ConfigWindow();

// Make globally accessible
window.configWindow = configWindow;

// Set up keyboard shortcut when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    ConfigWindow.setupKeyboardShortcut();
  });
} else {
  // DOM is already loaded
  ConfigWindow.setupKeyboardShortcut();
}